import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../services/unified';
import { useToast } from './ToastContext';
import { queryKeys } from '../lib/queryClient';
import type {
  JobFormState,
  EnhancedJobStatus,
  CreateEnhancedJobData,
  Job,
} from '../services/unified/types';

// ============================================================================
// Context Types
// ============================================================================

interface JobFormContextValue {
  // Form State
  formData: JobFormState;
  isDirty: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  
  // Form Actions
  updateField: <K extends keyof JobFormState>(field: K, value: JobFormState[K]) => void;
  updateMultipleFields: (fields: Partial<JobFormState>) => void;
  resetForm: () => void;
  loadFormData: (data: Partial<JobFormState>) => void;
  
  // Validation
  validateForm: () => boolean;
  validateField: (field: keyof JobFormState) => string | null;
  clearFieldError: (field: keyof JobFormState) => void;
  clearAllErrors: () => void;
  
  // Completeness
  calculateCompleteness: () => number;
  
  // Draft Management
  saveAsDraft: () => Promise<Job | null>;
  loadDraft: (draftId: string) => Promise<void>;
  
  // Publishing
  publishJob: () => Promise<Job | null>;
  
  // Template Management
  applyTemplate: (templateId: string) => Promise<void>;
}

// ============================================================================
// Context Definition
// ============================================================================

const JobFormContext = createContext<JobFormContextValue | undefined>(undefined);

// ============================================================================
// Initial State
// ============================================================================

const getInitialFormState = (): JobFormState => ({
  // Basic Info
  title: '',
  description: '',
  company_id: undefined,
  company_name: undefined,
  location: '',
  
  // Compensation
  salary_min: undefined,
  salary_max: undefined,
  
  // Job Details
  job_type: 'full-time',
  experience_level: 'mid',
  requirements: [],
  benefits: [],
  
  // Educational Qualifications
  educational_qualifications: [],
  
  // Media
  company_logo_url: undefined,
  job_image_url: undefined,
  
  // Status
  status: 'draft',
  is_featured: false,
  
  // Template
  template_id: undefined,
  
  // Metadata
  completeness_percentage: 0,
  validation_errors: {},
});

// ============================================================================
// Provider Props
// ============================================================================

interface JobFormProviderProps {
  children: React.ReactNode;
  initialData?: Partial<JobFormState>;
  jobId?: string; // For editing existing jobs
  autoSaveInterval?: number; // Auto-save interval in milliseconds (default: 30000 = 30 seconds)
}

// ============================================================================
// Provider Component
// ============================================================================

export const JobFormProvider: React.FC<JobFormProviderProps> = ({
  children,
  initialData,
  jobId,
  autoSaveInterval = 30000,
}) => {
  const { showSuccess, showError, showWarning } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<JobFormState>(() => ({
    ...getInitialFormState(),
    ...initialData,
  }));
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedDataRef = useRef<string>(JSON.stringify(formData));

  // ============================================================================
  // Field Update Functions
  // ============================================================================

  const updateField = useCallback(<K extends keyof JobFormState>(
    field: K,
    value: JobFormState[K]
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Clear validation error for this field
      if (updated.validation_errors[field as string]) {
        const { [field as string]: _, ...rest } = updated.validation_errors;
        updated.validation_errors = rest;
      }
      
      return updated;
    });
    setIsDirty(true);
  }, []);

  const updateMultipleFields = useCallback((fields: Partial<JobFormState>) => {
    setFormData((prev) => {
      const updated = { ...prev, ...fields };
      
      // Clear validation errors for updated fields
      const updatedErrors = { ...updated.validation_errors };
      Object.keys(fields).forEach((key) => {
        delete updatedErrors[key];
      });
      updated.validation_errors = updatedErrors;
      
      return updated;
    });
    setIsDirty(true);
  }, []);

  const resetForm = useCallback(() => {
    setFormData(getInitialFormState());
    setIsDirty(false);
    lastSavedDataRef.current = JSON.stringify(getInitialFormState());
  }, []);

  const loadFormData = useCallback((data: Partial<JobFormState>) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
    setIsDirty(false);
    lastSavedDataRef.current = JSON.stringify({ ...formData, ...data });
  }, [formData]);

  // ============================================================================
  // Validation Functions
  // ============================================================================

  const validateField = useCallback((field: keyof JobFormState): string | null => {
    const value = formData[field];
    
    switch (field) {
      case 'title':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Job title is required';
        }
        if (typeof value === 'string' && value.length < 3) {
          return 'Job title must be at least 3 characters';
        }
        if (typeof value === 'string' && value.length > 200) {
          return 'Job title must be less than 200 characters';
        }
        break;
        
      case 'description':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Job description is required';
        }
        if (typeof value === 'string' && value.length < 50) {
          return 'Job description must be at least 50 characters';
        }
        break;
        
      case 'location':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Location is required';
        }
        break;
        
      case 'salary_min':
        if (value !== undefined && typeof value === 'number' && value < 0) {
          return 'Minimum salary cannot be negative';
        }
        if (formData.salary_max !== undefined && value !== undefined && 
            typeof value === 'number' && typeof formData.salary_max === 'number' &&
            value > formData.salary_max) {
          return 'Minimum salary cannot be greater than maximum salary';
        }
        break;
        
      case 'salary_max':
        if (value !== undefined && typeof value === 'number' && value < 0) {
          return 'Maximum salary cannot be negative';
        }
        if (formData.salary_min !== undefined && value !== undefined &&
            typeof value === 'number' && typeof formData.salary_min === 'number' &&
            value < formData.salary_min) {
          return 'Maximum salary cannot be less than minimum salary';
        }
        break;
        
      case 'requirements':
        if (!Array.isArray(value) || value.length === 0) {
          return 'At least one requirement is needed';
        }
        break;
        
      default:
        return null;
    }
    
    return null;
  }, [formData]);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate required fields
    const requiredFields: (keyof JobFormState)[] = [
      'title',
      'description',
      'location',
      'requirements',
    ];
    
    requiredFields.forEach((field) => {
      const error = validateField(field);
      if (error) {
        errors[field] = error;
      }
    });
    
    // Validate optional fields that have values
    if (formData.salary_min !== undefined) {
      const error = validateField('salary_min');
      if (error) errors.salary_min = error;
    }
    
    if (formData.salary_max !== undefined) {
      const error = validateField('salary_max');
      if (error) errors.salary_max = error;
    }
    
    setFormData((prev) => ({
      ...prev,
      validation_errors: errors,
    }));
    
    return Object.keys(errors).length === 0;
  }, [formData, validateField]);

  const clearFieldError = useCallback((field: keyof JobFormState) => {
    setFormData((prev) => {
      const { [field as string]: _, ...rest } = prev.validation_errors;
      return {
        ...prev,
        validation_errors: rest,
      };
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      validation_errors: {},
    }));
  }, []);

  // ============================================================================
  // Completeness Calculation
  // ============================================================================

  const calculateCompleteness = useCallback((): number => {
    const weights = {
      // Required fields (60% total)
      title: 15,
      description: 15,
      location: 10,
      requirements: 10,
      company_name: 10,
      
      // Important optional fields (30% total)
      salary_min: 5,
      salary_max: 5,
      job_type: 5,
      experience_level: 5,
      benefits: 5,
      company_logo_url: 5,
      
      // Nice-to-have fields (10% total)
      job_image_url: 5,
      educational_qualifications: 5,
    };
    
    let totalScore = 0;
    
    // Check each field
    if (formData.title && formData.title.trim()) totalScore += weights.title;
    if (formData.description && formData.description.trim() && formData.description.length >= 50) {
      totalScore += weights.description;
    }
    if (formData.location && formData.location.trim()) totalScore += weights.location;
    if (formData.requirements && formData.requirements.length > 0) totalScore += weights.requirements;
    if (formData.company_name && formData.company_name.trim()) totalScore += weights.company_name;
    if (formData.salary_min !== undefined && formData.salary_min > 0) totalScore += weights.salary_min;
    if (formData.salary_max !== undefined && formData.salary_max > 0) totalScore += weights.salary_max;
    if (formData.job_type) totalScore += weights.job_type;
    if (formData.experience_level) totalScore += weights.experience_level;
    if (formData.benefits && formData.benefits.length > 0) totalScore += weights.benefits;
    if (formData.company_logo_url) totalScore += weights.company_logo_url;
    if (formData.job_image_url) totalScore += weights.job_image_url;
    if (formData.educational_qualifications && formData.educational_qualifications.length > 0) {
      totalScore += weights.educational_qualifications;
    }
    
    return Math.round(totalScore);
  }, [formData]); // Include formData in dependencies

  // ============================================================================
  // Draft Management
  // ============================================================================

  const saveAsDraft = useCallback(async (): Promise<Job | null> => {
    try {
      setIsSaving(true);
      
      // Prepare job data
      const jobData: CreateEnhancedJobData = {
        title: formData.title || 'Untitled Draft',
        description: formData.description,
        location: formData.location,
        company: formData.company_name,
        company_id: formData.company_id,
        company_logo_url: formData.company_logo_url,
        job_image_url: formData.job_image_url,
        salary_min: formData.salary_min,
        salary_max: formData.salary_max,
        job_type: formData.job_type,
        experience_level: formData.experience_level,
        requirements: formData.requirements,
        benefits: formData.benefits,
        status: 'draft',
        is_featured: formData.is_featured,
        educational_qualifications: formData.educational_qualifications,
      };
      
      let savedJob: Job;
      
      if (jobId) {
        // Update existing job/draft
        savedJob = await unifiedApiService.jobs.updateJob(jobId, jobData);
      } else {
        // Create new draft
        savedJob = await unifiedApiService.jobs.saveJobDraft(jobData);
      }
      
      // Update last saved data reference
      lastSavedDataRef.current = JSON.stringify(formData);
      setIsDirty(false);
      
      // Invalidate jobs cache so the list refreshes
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      
      showSuccess('Draft Saved', 'Your job draft has been saved successfully');
      
      return savedJob;
    } catch (error) {
      console.error('Failed to save draft:', error);
      showError('Save Failed', 'Failed to save job draft. Please try again.');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [formData, jobId, showSuccess, showError]);

  const loadDraft = useCallback(async (draftId: string): Promise<void> => {
    try {
      const draft = await unifiedApiService.jobs.loadJobDraft(draftId);
      
      // Map job data to form state
      loadFormData({
        title: draft.title || '',
        description: draft.description || '',
        location: draft.location || '',
        company_name: draft.company || '',
        company_id: (draft as any).company_id,
        company_logo_url: draft.company_logo_url,
        job_image_url: draft.job_image_url,
        salary_min: draft.salary_min,
        salary_max: draft.salary_max,
        job_type: draft.job_type,
        experience_level: draft.experience_level,
        requirements: draft.requirements || [],
        benefits: draft.benefits || [],
        status: (draft.status as EnhancedJobStatus) || 'draft',
        is_featured: (draft as any).is_featured || false,
        educational_qualifications: (draft as any).educational_qualifications || [],
        template_id: (draft as any).template_id,
      });
      
      showSuccess('Draft Loaded', 'Job draft loaded successfully');
    } catch (error) {
      console.error('Failed to load draft:', error);
      showError('Load Failed', 'Failed to load job draft. Please try again.');
    }
  }, [loadFormData, showSuccess, showError]);

  // ============================================================================
  // Publishing
  // ============================================================================

  const publishJob = useCallback(async (): Promise<Job | null> => {
    try {
      // Validate form before publishing
      if (!validateForm()) {
        showWarning('Validation Failed', 'Please fix all errors before publishing');
        return null;
      }
      
      setIsPublishing(true);
      
      // Prepare job data
      const jobData: CreateEnhancedJobData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        company: formData.company_name,
        company_id: formData.company_id,
        company_logo_url: formData.company_logo_url,
        job_image_url: formData.job_image_url,
        salary_min: formData.salary_min,
        salary_max: formData.salary_max,
        job_type: formData.job_type,
        experience_level: formData.experience_level,
        requirements: formData.requirements,
        benefits: formData.benefits,
        status: 'active', // Set to active when publishing
        is_featured: formData.is_featured,
        educational_qualifications: formData.educational_qualifications,
      };
      
      let publishedJob: Job;
      
      if (jobId) {
        // Update existing job
        publishedJob = await unifiedApiService.jobs.updateJob(jobId, jobData);
      } else {
        // Create new job
        publishedJob = await unifiedApiService.jobs.createJob(jobData);
      }
      
      // Reset dirty state
      lastSavedDataRef.current = JSON.stringify(formData);
      setIsDirty(false);
      
      // Invalidate jobs cache so the list refreshes
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
      
      showSuccess('Job Published', 'Your job has been published successfully');
      
      return publishedJob;
    } catch (error) {
      console.error('Failed to publish job:', error);
      showError('Publish Failed', 'Failed to publish job. Please try again.');
      return null;
    } finally {
      setIsPublishing(false);
    }
  }, [formData, jobId, validateForm, showSuccess, showError, showWarning]);

  // ============================================================================
  // Template Management
  // ============================================================================

  const applyTemplate = useCallback(async (templateId: string): Promise<void> => {
    try {
      const template = await unifiedApiService.jobTemplates.getTemplateById(templateId);
      
      // Apply template data to form
      updateMultipleFields({
        title: template.job_data.title || '',
        description: template.job_data.description || '',
        location: template.job_data.location || '',
        job_type: template.job_data.job_type,
        experience_level: template.job_data.experience_level,
        requirements: template.job_data.requirements || [],
        benefits: template.job_data.benefits || [],
        template_id: templateId,
      });
      
      showSuccess('Template Applied', `Template "${template.name}" has been applied`);
    } catch (error) {
      console.error('Failed to apply template:', error);
      showError('Template Failed', 'Failed to apply template. Please try again.');
    }
  }, [updateMultipleFields, showSuccess, showError]);

  // ============================================================================
  // Auto-save Effect
  // ============================================================================

  useEffect(() => {
    // Only auto-save if form is dirty and not currently saving/publishing
    if (!isDirty || isSaving || isPublishing) {
      return;
    }
    
    // Don't auto-save if form has no meaningful data (prevents saving empty forms)
    if (!formData.title && !formData.description && !formData.company_id) {
      return;
    }
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for auto-save
    autoSaveTimerRef.current = setTimeout(() => {
      const currentData = JSON.stringify(formData);
      
      // Only save if data has actually changed
      if (currentData !== lastSavedDataRef.current) {
        saveAsDraft();
      }
    }, autoSaveInterval);
    
    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, isDirty, isSaving, isPublishing, autoSaveInterval, saveAsDraft]);

  // ============================================================================
  // Completeness Calculation Effect
  // ============================================================================

  useEffect(() => {
    const percentage = calculateCompleteness();
    
    // Update formData with new completeness percentage
    setFormData((prev) => {
      if (prev.completeness_percentage !== percentage) {
        return {
          ...prev,
          completeness_percentage: percentage,
        };
      }
      return prev;
    });
  }, [
    formData.title,
    formData.description,
    formData.location,
    formData.requirements,
    formData.company_name,
    formData.salary_min,
    formData.salary_max,
    formData.job_type,
    formData.experience_level,
    formData.benefits,
    formData.company_logo_url,
    formData.job_image_url,
    formData.educational_qualifications,
    calculateCompleteness,
  ]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: JobFormContextValue = {
    // Form State
    formData,
    isDirty,
    isSaving,
    isPublishing,
    
    // Form Actions
    updateField,
    updateMultipleFields,
    resetForm,
    loadFormData,
    
    // Validation
    validateForm,
    validateField,
    clearFieldError,
    clearAllErrors,
    
    // Completeness
    calculateCompleteness,
    
    // Draft Management
    saveAsDraft,
    loadDraft,
    
    // Publishing
    publishJob,
    
    // Template Management
    applyTemplate,
  };

  return (
    <JobFormContext.Provider value={contextValue}>
      {children}
    </JobFormContext.Provider>
  );
};

// ============================================================================
// Hook for using the context
// ============================================================================

export const useJobForm = (): JobFormContextValue => {
  const context = useContext(JobFormContext);
  if (context === undefined) {
    throw new Error('useJobForm must be used within a JobFormProvider');
  }
  return context;
};
